/**
 * Invoice API (End Customer)
 *
 * Faturas mensais consolidadas recebidas pelo end customer. Cada fatura
 * consolida o consumo/cobrança de um mês. Pré-pago: extrato do consumo
 * descontado do crédito. Pós-pago: valor a pagar do período.
 */

import { formatMoney } from '../utils'
import api from './api'
import type { MemoriaDeCalculo } from './specialContractsCustomerApi'

// ============================================================================
// Types
// ============================================================================

export type InvoiceStatus = 'draft' | 'issued' | 'paid' | 'overdue' | 'cancelled'
export type InvoiceBillingMode = 'postpaid' | 'direct'

export type InvoiceLineItem = {
  id: string
  description: string
  resource_type: string | null
  source: string
  reference_type: string | null
  reference_id: string | null
  quantity: number
  unit_price: number | null
  amount: number
  currency: string
  occurred_at: string | null
  /** #1091 F3: mês de competência (YYYY-MM) dos lançamentos por competência */
  competence_period?: string | null
  /** #1091 F3: o contrato especial que gerou a linha (link ao detalhe) */
  special_contract_id?: string | null
  special_contract_name?: string | null
  /** #1091 F3: memória de cálculo (allowlist do backend: dias, preços, revisões) */
  calculo?: MemoriaDeCalculo | null
}

export type InvoiceSummary = {
  id: string
  issuer_org_id: string
  recipient_org_id: string
  issuer_name: string | null
  recipient_name: string | null
  billing_period: string // "YYYY-MM"
  period_start: string
  period_end: string
  invoice_number: string
  billing_mode: InvoiceBillingMode
  status: InvoiceStatus
  subtotal: number
  discount_amount: number
  tax_amount: number
  total_amount: number
  currency: string
  opening_balance: number | null
  credits_added: number | null
  closing_balance: number | null
  issue_date: string | null
  due_date: string | null
  paid_date: string | null
  pdf_url: string | null
  created_at: string
}

export type InvoiceDetail = {
  notes: string | null
  line_items: InvoiceLineItem[]
} & InvoiceSummary

export type InvoiceListResponse = {
  invoices: InvoiceSummary[]
  total: number
  skip: number
  limit: number
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Lista as faturas mensais do customer (mais recentes primeiro).
 */
export const listInvoices = async (params?: {
  skip?: number
  limit?: number
}): Promise<InvoiceListResponse> => {
  const response = await api.get('/api/v1/end-customer/invoices', { params })
  return response.data
}

/**
 * Detalha uma fatura específica, com todas as linhas.
 */
export const getInvoice = async (invoiceId: string): Promise<InvoiceDetail> => {
  const response = await api.get(`/api/v1/end-customer/invoices/${invoiceId}`)
  return response.data
}

// --- Cloud Provider: faturas emitidas (CP → Reseller) ---

export const listProviderInvoices = async (params?: {
  skip?: number
  limit?: number
}): Promise<InvoiceListResponse> => {
  const response = await api.get('/api/v1/cloud-provider/invoices', { params })
  return response.data
}

export const getProviderInvoice = async (invoiceId: string): Promise<InvoiceDetail> => {
  const response = await api.get(`/api/v1/cloud-provider/invoices/${invoiceId}`)
  return response.data
}

// --- Reseller: faturas recebidas (do CP) e emitidas (aos clientes) ---

export const listResellerInvoices = async (params?: {
  direction?: 'received' | 'issued'
  /** Só em direction='issued': faturas de um cliente (link vindo do contrato). */
  recipient_id?: string
  skip?: number
  limit?: number
}): Promise<InvoiceListResponse> => {
  const response = await api.get('/api/v1/reseller/invoices', { params })
  return response.data
}

export const getResellerInvoice = async (invoiceId: string): Promise<InvoiceDetail> => {
  const response = await api.get(`/api/v1/reseller/invoices/${invoiceId}`)
  return response.data
}

// ============================================================================
// Helper Functions
// ============================================================================

const MONTHS_PT = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
]

/**
 * Formata "2026-06" para "Junho/2026".
 */
export const formatBillingPeriod = (billingPeriod: string): string => {
  const [year, month] = billingPeriod.split('-')
  const idx = Number.parseInt(month, 10) - 1
  if (idx < 0 || idx > 11) return billingPeriod
  return `${MONTHS_PT[idx]}/${year}`
}

/**
 * Formata valor monetário para exibição.
 *
 * Delega ao helper do `utils` para não haver duas formatações de dinheiro
 * divergindo entre a tela de faturas e a de contratos.
 */
export const formatCurrency = (value: number, currency: string = 'BRL'): string =>
  formatMoney(value, currency)

/**
 * Label do status da fatura.
 */
export const getInvoiceStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    draft: 'Rascunho',
    issued: 'Emitida',
    paid: 'Paga',
    overdue: 'Vencida',
    cancelled: 'Cancelada',
  }
  return labels[status] || status
}

/**
 * Cor do badge do status da fatura.
 */
export const getInvoiceStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-800',
    issued: 'bg-blue-100 text-blue-800',
    paid: 'bg-green-100 text-green-800',
    overdue: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-500',
  }
  return colors[status] || 'bg-gray-100 text-gray-800'
}

/**
 * Rótulo amigável para a fatura conforme o modo de cobrança.
 * Pré-pago: "consumo descontado do crédito". Pós-pago: "a pagar".
 */
export const getInvoiceAmountLabel = (billingMode: string): string => {
  return billingMode === 'direct' ? 'Total a pagar' : 'Consumo do período'
}

// ============================================================================
// Export default object
// ============================================================================

const invoiceApi = {
  listInvoices,
  getInvoice,
  listProviderInvoices,
  getProviderInvoice,
  listResellerInvoices,
  getResellerInvoice,
  // Helper functions
  formatBillingPeriod,
  formatCurrency,
  getInvoiceStatusLabel,
  getInvoiceStatusColor,
  getInvoiceAmountLabel,
}

export default invoiceApi
